import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { LockOpen, RotateCcw, TriangleAlert } from "lucide-react";
import { api } from "../lib/api";

const errText = (e, f) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || f; };

/** Reason + confirm prompt shared by every reopen (signed ticket, signed delivery, installed quantity). */
export function ReopenPrompt({ title, body, qty = null, onQty = null, max = null, busy, onCancel, onConfirm, testId }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  return (
    <div className="mt-2 border border-amber-300 bg-amber-50 rounded p-2 space-y-1.5 text-[12px] text-amber-900"
         role="alertdialog" data-testid={testId}>
      <div className="font-semibold flex items-center gap-1"><LockOpen className="w-3.5 h-3.5" /> {title}</div>
      <div>{body}</div>
      {onQty && (
        <label className="flex items-center gap-1">Quantity being reopened
          <Input type="number" min="0" max={max ?? undefined} step="any" value={qty} onChange={(e) => onQty(e.target.value)}
                 className="h-8 w-20 text-right" data-testid={`${testId}-qty`} />
          {max != null && <span className="text-amber-800">of {max} installed</span>}
        </label>
      )}
      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for reopening (required)"
             className="h-9" data-testid={`${testId}-reason`} />
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
             className="h-9" data-testid={`${testId}-note`} />
      <div className="flex flex-wrap gap-1">
        <Button size="sm" className="min-h-[40px]" disabled={busy || !reason.trim()}
                onClick={() => onConfirm({ reason: reason.trim(), note: note.trim() })} data-testid={`${testId}-confirm`}>
          Reopen for Editing
        </Button>
        <Button size="sm" variant="ghost" className="min-h-[40px]" onClick={onCancel} data-testid={`${testId}-cancel`}>Cancel</Button>
      </div>
      <div className="text-[11px]">The signed record, its signature and every original placement stay in history.</div>
    </div>
  );
}

/** Return to Pull In Progress + Reopen Pull Ticket for Editing, for one ticket. */
export default function TicketEditActions({ ticket, actor, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState(false);
  const status = ticket?.status || "";
  const reopened = ticket?.reopened || null;
  const pullReopened = ticket?.pull_reopened || null;

  const returnToPull = async (confirm = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/warehouse/tickets/${ticket.id}/return-to-pull`, { by: actor, confirm });
      toast.success("Back to Pull In Progress", { description: data.message });
      await onChanged?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.error === "confirm_required" && window.confirm(`${d.message}\n\nContinue?`)) return returnToPull(true);
      toast.error(errText(e, "Could not reopen the pull"));
    } finally { setBusy(false); }
  };
  const reopen = async ({ reason, note }) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/reopen`, { by: actor, kind: "picking", reason, note, confirm: true });
      setPrompt(false);
      toast.success("Pull ticket reopened for editing", { description: data.message });
      await onChanged?.();
    } catch (e) { toast.error(errText(e, "Could not reopen the ticket")); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-2 space-y-1" data-testid={`wh-edit-actions-${ticket.ticket_number}`}>
      {pullReopened && status !== "Ready for Delivery" && (
        <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-1"
             data-testid="wh-not-ready-banner">
          <TriangleAlert className="w-3 h-3" /> Not ready for delivery — the warehouse is editing the pull
          {pullReopened.assignee_kept ? ` (${pullReopened.assignee_kept.name} is still the delivery employee)` : ""}. Mark Ready for Delivery again when the pull is right.
        </div>
      )}
      {reopened && (
        <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded px-2 py-1" data-testid="wh-reopened-banner">
          <b>Reopened for Editing</b> by {reopened.by} — {reopened.reason}. The signed delivery is kept in history; review and
          sign off again once the corrections are done.
        </div>
      )}
      <div className="flex flex-wrap gap-1">
        {status === "Ready for Delivery" && (
          <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => returnToPull(false)}
                  data-testid={`wh-return-to-pull-${ticket.ticket_number}`}>
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Return to Pull In Progress
          </Button>
        )}
        {status === "Closed" && !prompt && (
          <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => setPrompt(true)}
                  data-testid={`wh-reopen-${ticket.ticket_number}`}>
            <LockOpen className="w-3.5 h-3.5 mr-1" /> Reopen Pull Ticket for Editing
          </Button>
        )}
      </div>
      {prompt && (
        <ReopenPrompt title="Reopen this signed pull ticket?" busy={busy} onCancel={() => setPrompt(false)} onConfirm={reopen}
                      testId="wh-reopen-prompt"
                      body="The delivery has been signed for, so the pull record is read-only. Reopening lets any warehouse user
                            correct it; it has to be reviewed and closed again afterwards." />
      )}
    </div>
  );
}
